import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { API_BASE_URL } from "../../utils/api";
import { fetchFeeAssignments } from "../shared/fees/feeApi";
import {
  ArrowLeftIcon,
  SearchIcon,
  AlertIcon,
  DownloadIcon,
} from "../shared/icons/Icons";

function CsvExportPage({ onLogout, showToast }) {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [classFilter, setClassFilter] = useState("");
  const [campusFilter, setCampusFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [campuses, setCampuses] = useState([]);
  const [exportRows, setExportRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [hasAppliedFilters, setHasAppliedFilters] = useState(false);
  const [filterError, setFilterError] = useState("");

  useEffect(() => {
    const fetchCampuses = async () => {
      const token = localStorage.getItem("als_token");
      try {
        const res = await fetch(`${API_BASE_URL}/api/campuses`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 401) {
          onLogout?.();
          return;
        }
        if (!res.ok) throw new Error("Failed to fetch campuses");
        const data = await res.json();
        setCampuses(Array.isArray(data) ? data : []);
      } catch (err) {
        console.error("Failed to fetch campuses", err);
        setCampuses([]);
      }
    };

    fetchCampuses();
  }, [onLogout]);

  const classes = [
    "Nursery",
    "PG",
    "KG",
    "I",
    "II",
    "III",
    "IV",
    "V",
    "VI",
    "VII",
    "VIII",
    "IX",
    "X",
  ];
  const statusOptions = ["Active", "Inactive", "Withdrawn"];
  const hasAnyFilter = Boolean(
    searchTerm.trim() || classFilter || campusFilter || statusFilter,
  );

  const csvColumns = [
    ["id", "Database ID"],
    ["student_id", "Student ID"],
    ["name", "Name"],
    ["gender", "Gender"],
    ["date_of_birth", "Date Of Birth"],
    ["campus_id", "Campus ID"],
    ["campus_name", "Campus Name"],
    ["class_id", "Class ID"],
    ["class_name", "Class Name"],
    ["class_section", "Class Section"],
    ["admission_date", "Admission Date"],
    ["student_status", "Student Status"],
    ["fee_plan_id", "Fee Plan ID"],
    ["fee_plan_name", "Fee Plan Name"],
    ["scholarship_id", "Scholarship ID"],
    ["scholarship_name", "Scholarship Name"],
    ["fee_status", "Fee Status"],
    ["actual_fee_amount", "Actual Fee Amount"],
    ["fee_assignment_category", "Fee Category"],
    ["fee_assignment_month", "Fee Month"],
    ["fee_assignment_session", "Fee Academic Session"],
    ["fee_assignment_year", "Fee Academic Year"],
    ["fee_assignment_due_date", "Fee Due Date"],
    ["fee_assignment_subtotal", "Fee Subtotal"],
    ["fee_assignment_concession", "Fee Discount"],
    ["fee_assignment_net_payable", "Fee Net Payable"],
    ["fee_assignment_paid", "Fee Paid Amount"],
    ["fee_assignment_balance", "Fee Balance"],
    ["fee_assignment_status", "Fee Assignment Status"],
    ["fee_assignment_components", "Fee Components"],
    ["fee_assignment_last_updated", "Fee Last Updated"],
    ["withdrawal_date", "Withdrawal Date"],
    ["withdrawal_reason", "Withdrawal Reason"],
    ["created_at", "Created At"],
  ];

  const getAssignmentStatus = (assignment) => {
    return assignment.status || "Unpaid";
  };

  const getStudentFeeStatus = (student, entries) => {
    if (!entries.length) return student.fee_status || "Unpaid";

    const allPaid = entries.every(([, assignment]) => {
      const status = getAssignmentStatus(assignment);
      return status === "Paid" || status === "Waived";
    });

    return allPaid ? "Paid" : "Unpaid";
  };

  const getFeeRowsForStudent = (student, feeAssignments) => {
    const cats =
      feeAssignments[String(student.id)] ||
      feeAssignments[String(student.student_id)] ||
      {};
    const entries = Object.entries(cats || {});
    const studentFeeStatus = getStudentFeeStatus(student, entries);

    if (entries.length === 0) {
      return [{ ...student, fee_status: studentFeeStatus }];
    }

    return entries.map(([, assignment]) => {
      const subtotal = Number(assignment.subtotal || 0);
      const concession = Number(assignment.concessionAmount || 0);
      const netPayable = Number(assignment.netPayable || Math.max(subtotal - concession, 0));
      const assignmentStatus = getAssignmentStatus(assignment);
      const paidAmount = assignmentStatus === "Paid" || assignmentStatus === "Waived" ? netPayable : 0;
      const balance = assignmentStatus === "Paid" || assignmentStatus === "Waived" ? 0 : netPayable;
      const components = Array.isArray(assignment.components)
        ? assignment.components
            .map((component) => {
              const label = component.label || component.name || component.componentLabel || component.componentId;
              return `${label}: ${component.amount || 0}`;
            })
            .join("; ")
        : "";

      return {
        ...student,
        fee_status: assignmentStatus,
        fee_assignment_category:
          assignment.categoryLabel || assignment.categoryId || "",
        fee_assignment_month: assignment.month || "",
        fee_assignment_session: assignment.session || "",
        fee_assignment_year: assignment.academicYear || "",
        fee_assignment_due_date: assignment.dueDate || "",
        fee_assignment_subtotal: subtotal,
        fee_assignment_concession: concession,
        fee_assignment_net_payable: netPayable,
        fee_assignment_paid: paidAmount,
        fee_assignment_balance: balance,
        fee_assignment_status: assignmentStatus,
        fee_assignment_components: components,
        fee_assignment_last_updated: assignment.lastUpdated || "",
      };
    });
  };

  const fetchFilteredStudents = async () => {
    if (!hasAnyFilter) {
      setExportRows([]);
      setHasAppliedFilters(false);
      setFilterError("");
      return;
    }

    const token = localStorage.getItem("als_token");
    const params = new URLSearchParams();
    if (searchTerm.trim()) params.set("search", searchTerm.trim());
    if (classFilter) params.set("class_name", classFilter);
    if (statusFilter) params.set("student_status", statusFilter);
    if (campusFilter) params.set("campus_id", campusFilter);

    setIsLoading(true);
    setFilterError("");
    setHasAppliedFilters(true);

    try {
      const res = await fetch(
        `${API_BASE_URL}/api/students?${params.toString()}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      if (res.status === 401) {
        onLogout?.();
        return;
      }
      if (!res.ok) {
        const errData = await res.json().catch(() => ({}));
        throw new Error(errData.detail || "Unable to load filtered students");
      }
      const data = await res.json();
      const feeAssignments = await fetchFeeAssignments();
      setExportRows(
        Array.isArray(data)
          ? data.flatMap((s) =>
              getFeeRowsForStudent(
                { ...s, id: s.id, student_id: s.student_id },
                feeAssignments,
              ),
            )
          : [],
      );
    } catch (err) {
      console.error(err);
      setExportRows([]);
      setFilterError(err.message || "Unable to load filtered students");
    } finally {
      setIsLoading(false);
    }
  };

  const clearFilters = () => {
    setSearchTerm("");
    setClassFilter("");
    setCampusFilter("");
    setStatusFilter("");
    setExportRows([]);
    setHasAppliedFilters(false);
    setFilterError("");
  };

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return "";
    const text = String(value);
    return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
  };

  const downloadCsv = () => {
    if (!exportRows.length) return;
    const header = csvColumns.map(([, label]) => label);
    const rows = exportRows.map((student) =>
      csvColumns.map(([key]) => escapeCsvValue(student[key])),
    );
    const csvContent = [header.map(escapeCsvValue), ...rows]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([`\uFEFF${csvContent}`], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `students_export_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    showToast?.(
      "success",
      "CSV Downloaded",
      `${exportRows.length} student record${exportRows.length === 1 ? "" : "s"} exported.`,
    );
  };

  return (
    <div className="form-page">
      <div className="form-page-header">
        <button
          className="back-btn"
          onClick={() => navigate("/dashboard")}
        >
          <ArrowLeftIcon />
        </button>
        <div>
          
      
        </div>
      </div>

      <div className="delete-section">
        <div className="student-filter-panel">
          <div>
            <h3>Filter Student Data</h3>
          
          </div>
          {hasAppliedFilters && !isLoading && (
            <span className="student-result-count">
              {exportRows.length} CSV row{exportRows.length === 1 ? "" : "s"}{" "}
              ready
            </span>
          )}
        </div>

        <div className="search-filters-row student-record-filter-row">
          <div className="search-bar">
            <div className="search-input-wrapper">
              <SearchIcon />
              <input
                type="text"
                className="search-input"
                placeholder="Search by name or student ID..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>
          </div>

          <div className="filter-controls student-record-filter-controls">
            <select
              className="filter-select"
              value={classFilter}
              onChange={(e) => setClassFilter(e.target.value)}
            >
              <option value="">All Classes</option>
              {classes.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <select
              className="filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="">All Statuses</option>
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <select
              className="filter-select"
              value={campusFilter}
              onChange={(e) => setCampusFilter(e.target.value)}
            >
              <option value="">All Campuses</option>
              {campuses.map((c) => (
                <option key={c.id} value={c.id.toString()}>
                  {c.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn-primary filter-apply-btn"
              onClick={fetchFilteredStudents}
              disabled={!hasAnyFilter || isLoading}
              title={
                !hasAnyFilter
                  ? "Select at least one filter first"
                  : "Load filtered students"
              }
            >
              <span>{isLoading ? "Loading..." : "Apply Filters"}</span>
            </button>
            <button
              type="button"
              className="btn-secondary filter-clear-btn"
              onClick={clearFilters}
              disabled={!hasAnyFilter && !hasAppliedFilters}
            >
              Clear
            </button>
            <button
              type="button"
              className="btn-primary csv-download-btn"
              onClick={downloadCsv}
              disabled={exportRows.length === 0 || isLoading}
              title={
                exportRows.length === 0
                  ? "Apply filters before downloading"
                  : "Download filtered CSV"
              }
            >
              <span>Download CSV</span>
            </button>
          </div>
        </div>

        <div className="student-table-card">
          {isLoading ? (
            <div className="student-loading-state">
              <div className="student-loading-spinner" />
              <h3>Preparing export data</h3>
              <p>Fetching only the students that match your filters.</p>
            </div>
          ) : filterError ? (
            <div className="empty-state">
              <div className="empty-state-icon">
                <AlertIcon />
              </div>
              <h3>Could Not Load Export</h3>
              <p>{filterError}</p>
            </div>
          ) : !hasAppliedFilters ? (
            <div className="empty-state student-filter-empty">
              <div className="empty-state-icon">
                <DownloadIcon />
              </div>
              <h3>Apply Filters To Prepare CSV</h3>
              <p>
                Use search, class, campus, or status filters above. The full
                database is not exported by default.
              </p>
            </div>
          ) : exportRows.length > 0 ? (
            <table className="student-table filtered-student-table">
              <thead>
                <tr>
                  <th>Student ID</th>
                  <th>Name</th>
                  <th>Campus</th>
                  <th>Class</th>
                  <th>Status</th>
                  <th>Fee Month</th>
                  <th>Fee Amount</th>
                </tr>
              </thead>
              <tbody>
                {exportRows.slice(0, 25).map((student, index) => (
                  <tr key={`${student.id}-${student.fee_assignment_category || "student"}-${student.fee_assignment_month || index}`}>
                    <td>
                      <strong>{student.student_id || student.id}</strong>
                    </td>
                    <td>{student.name || "-"}</td>
                    <td>{student.campus_name || student.campus_id || "-"}</td>
                    <td>{student.class_name || "-"}</td>
                    <td>{student.student_status || "-"}</td>
                    <td>{student.fee_assignment_month || "-"}</td>
                    <td>
                      {student.fee_assignment_net_payable ||
                      student.actual_fee_amount ||
                      "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">
                <SearchIcon />
              </div>
              <h3>No Students Found</h3>
              <p>No database records matched the selected filters.</p>
            </div>
          )}
          {exportRows.length > 25 && (
            <p className="csv-preview-note">
              Showing first 25 records in preview. The CSV will include all{" "}
              {exportRows.length} filtered CSV rows, including fee details.
            </p>
          )}
        </div>
      </div>
    </div>
  );
}

export default CsvExportPage;
